/**
 * LearningTab — trainer-facing self-learning assignments (Phase 2A).
 * List with progress rollups + "Assign Learning" wizard + per-trainee drill-down.
 */
import { useEffect, useState, useCallback } from 'react';
import {
  Plus, Trash2, BookOpen, CheckCircle2, Clock, AlertTriangle, Award, Timer,
  Presentation, FileText, Video, Youtube, Link2, X, MessageCircleQuestion,
  Package, UploadCloud,
} from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Progress } from '../../components/ui/progress';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { TraineeFilterBar, useTraineeFilter, ALL } from './TraineeFilterContext';
import QADialog from './QADialog';

const TYPE_ICON = { module: Presentation, document: FileText, video: Video, youtube: Youtube, link: Link2, scorm: Package };
const NONE = '__none__';

export default function LearningTab() {
  const { get, del } = useApi();
  const { hasRole } = useAuth();
  const canWrite = hasRole('trainer');
  const { departmentId, batchId } = useTraineeFilter();

  const [assignments, setAssignments] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openWizard, setOpenWizard] = useState(false);
  const [detail, setDetail] = useState(null);
  const [openQA, setOpenQA] = useState(false);

  const scopeQS = useCallback(() => {
    const p = new URLSearchParams();
    if (batchId !== ALL) p.set('batch_id', batchId);
    else if (departmentId !== ALL) p.set('department_id', departmentId);
    const s = p.toString();
    return s ? `?${s}` : '';
  }, [batchId, departmentId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, o] = await Promise.all([
        get(`/learning-assignments${scopeQS()}`),
        get(`/learning-assignments/overview${scopeQS()}`),
      ]);
      setAssignments(a.assignments || []);
      setOverview(o);
    } catch { toast.error('Failed to load learning assignments'); }
    finally { setLoading(false); }
  }, [get, scopeQS]);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (a) => {
    try {
      await del(`/learning-assignments/${a.assignment_id}`);
      toast.success('Assignment deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="space-y-4" data-testid="learning-tab">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <TraineeFilterBar />
        {canWrite && (
          <Button onClick={() => setOpenWizard(true)} data-testid="assign-learning-btn">
            <Plus className="w-4 h-4 mr-1" /> Assign Learning
          </Button>
        )}
      </div>

      {overview && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <Stat icon={BookOpen} label="Assigned" value={overview.assigned} testid="lstat-assigned" />
          <Stat icon={CheckCircle2} label="Completed" value={overview.completed} testid="lstat-completed" />
          <Stat icon={Clock} label="Pending" value={overview.pending} testid="lstat-pending" />
          <Stat icon={AlertTriangle} label="Overdue" value={overview.overdue} testid="lstat-overdue" />
          <Stat icon={Award} label="Avg Quiz" value={overview.avg_quiz_score != null ? `${overview.avg_quiz_score}%` : '—'} testid="lstat-quiz" />
          <Stat icon={Timer} label="Learning Hrs" value={overview.learning_hours} testid="lstat-hours" />
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : assignments.length === 0 ? (
        <Card className="p-10 text-center">
          <BookOpen className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
          <p className="font-display font-semibold">No learning assigned yet</p>
          <p className="text-sm text-muted-foreground mt-1">
            Assign PPT modules, documents, videos, links or SCORM/xAPI courses for self-paced learning.
          </p>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="trainer-assignment-list">
          {assignments.map(a => (
            <Card key={a.assignment_id} className="p-4" data-testid={`trainer-assignment-${a.assignment_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <button className="text-left min-w-0" onClick={() => setDetail(a.assignment_id)}
                        data-testid={`open-assignment-${a.assignment_id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display font-semibold">{a.title}</h3>
                    {a.overdue && <Badge className="bg-rose-100 text-rose-800 border-0">Overdue</Badge>}
                    {a.quiz_id && <Badge variant="outline" className="text-[10px]">Quiz</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {a.items?.length} item{a.items?.length === 1 ? '' : 's'}
                    {a.due_date && <> · Due {new Date(a.due_date).toLocaleDateString()}</>}
                    {' '}· {a.completed_count}/{a.target_count} completed
                  </p>
                </button>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 w-40">
                    <Progress value={a.avg_progress} className="h-2" />
                    <span className="text-xs font-medium">{Math.round(a.avg_progress)}%</span>
                  </div>
                  {canWrite && (
                    <Button size="sm" variant="ghost" onClick={() => onDelete(a)}
                            data-testid={`delete-assignment-${a.assignment_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {openWizard && <AssignWizard onClose={(saved) => { setOpenWizard(false); if (saved) load(); }} />}
      {detail && <DetailDialog assignmentId={detail} onClose={() => setDetail(null)} />}
      {openQA && <QADialog onClose={() => setOpenQA(false)} />}
    </div>
  );
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <Card className="p-3 flex items-center gap-3" data-testid={testid}>
      <div className="w-8 h-8 rounded-lg bg-amber-500/10 text-amber-600 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <p className="text-lg font-bold font-display leading-none">{value}</p>
        <p className="text-[11px] text-muted-foreground mt-0.5">{label}</p>
      </div>
    </Card>
  );
}

function AssignWizard({ onClose }) {
  const { get, post } = useApi();
  const { departments, batches } = useTraineeFilter();

  const [form, setForm] = useState({
    title: '', description: '', department_id: '', batch_id: '',
    due_date: '', quiz_id: '',
  });
  const [targetMode, setTargetMode] = useState('batch'); // batch | selected
  const [traineeIds, setTraineeIds] = useState(new Set());
  const [trainees, setTrainees] = useState([]);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  // Picker sources
  const [modules, setModules] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [videos, setVideos] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [scormPkgs, setScormPkgs] = useState([]);
  const [scormUploading, setScormUploading] = useState(false);
  const [linkDraft, setLinkDraft] = useState({ type: 'link', url: '', title: '' });

  useEffect(() => {
    get('/modules?limit=200').then(r => setModules(r.modules || [])).catch(() => {});
    get('/documents').then(r => setDocuments(r.documents || [])).catch(() => {});
    get('/videos/library?limit=100').then(r => setVideos(r.files || r.videos || [])).catch(() => {});
    get('/quizzes').then(r => setQuizzes(r.quizzes || [])).catch(() => {});
    get('/scorm/packages').then(r => setScormPkgs(r.packages || [])).catch(() => {});
  }, [get]);

  const uploadScorm = async (file) => {
    if (!file) return;
    setScormUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const pkg = await post('/scorm/packages/upload', fd, { timeout: 300000 });
      setScormPkgs(list => [pkg, ...list]);
      addItem({ type: 'scorm', ref_id: pkg.package_id, title: pkg.title });
      toast.success(`${pkg.package_type === 'xapi' ? 'xAPI' : 'SCORM 1.2'} package uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Package upload failed');
    } finally { setScormUploading(false); }
  };

  useEffect(() => {
    if (!form.batch_id) { setTrainees([]); return; }
    get(`/trainees?limit=500&batch_id=${form.batch_id}`)
      .then(r => setTrainees((r.trainees || []).filter(t => t.batch_id === form.batch_id)))
      .catch(() => setTrainees([]));
  }, [get, form.batch_id]);

  const deptBatches = form.department_id
    ? batches.filter(b => b.department_id === form.department_id)
    : batches;

  const addItem = (item) => {
    setItems(list => [...list, item]);
    toast.success('Added to assignment');
  };

  const onSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.batch_id) { toast.error('Select a batch'); return; }
    if (items.length === 0) { toast.error('Add at least one learning item'); return; }
    if (targetMode === 'selected' && traineeIds.size === 0) {
      toast.error('Select at least one trainee'); return;
    }
    setSaving(true);
    try {
      await post('/learning-assignments', {
        title: form.title.trim(),
        description: form.description || null,
        department_id: form.department_id || null,
        batch_id: form.batch_id,
        trainee_ids: targetMode === 'selected' ? [...traineeIds] : null,
        items: items.map(({ type, ref_id, url, title }) => ({ type, ref_id, url, title })),
        due_date: form.due_date || null,
        quiz_id: form.quiz_id && form.quiz_id !== NONE ? form.quiz_id : null,
      });
      toast.success('Learning assigned');
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Publish failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="assign-wizard">
        <DialogHeader><DialogTitle>Assign Learning</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Department</Label>
              <Select value={form.department_id || NONE}
                      onValueChange={v => setForm(f => ({ ...f, department_id: v === NONE ? '' : v, batch_id: '' }))}>
                <SelectTrigger data-testid="wizard-department"><SelectValue placeholder="All departments" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>All departments</SelectItem>
                  {departments.map(d => <SelectItem key={d.department_id} value={d.department_id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Batch *</Label>
              <Select value={form.batch_id || undefined}
                      onValueChange={v => setForm(f => ({ ...f, batch_id: v }))}>
                <SelectTrigger data-testid="wizard-batch"><SelectValue placeholder="Select batch" /></SelectTrigger>
                <SelectContent>
                  {deptBatches.map(b => <SelectItem key={b.batch_id} value={b.batch_id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Assign to</Label>
            <div className="flex gap-2 mt-1">
              <Button type="button" size="sm" variant={targetMode === 'batch' ? 'default' : 'outline'}
                      onClick={() => setTargetMode('batch')} data-testid="target-batch-btn">Entire Batch</Button>
              <Button type="button" size="sm" variant={targetMode === 'selected' ? 'default' : 'outline'}
                      onClick={() => setTargetMode('selected')} data-testid="target-selected-btn">Selected Trainees</Button>
            </div>
            {targetMode === 'selected' && (
              <Card className="mt-2 max-h-40 overflow-y-auto divide-y" data-testid="trainee-picker">
                {trainees.length === 0 ? (
                  <p className="p-3 text-xs text-muted-foreground">Select a batch first.</p>
                ) : trainees.map(t => (
                  <label key={t.trainee_id} className="flex items-center gap-2 p-2 text-sm cursor-pointer hover:bg-muted/50">
                    <Checkbox checked={traineeIds.has(t.trainee_id)}
                              onCheckedChange={(c) => setTraineeIds(s => {
                                const n = new Set(s);
                                if (c) n.add(t.trainee_id); else n.delete(t.trainee_id);
                                return n;
                              })}
                              data-testid={`pick-trainee-${t.trainee_id}`} />
                    {t.name} {t.email && <span className="text-xs text-muted-foreground">({t.email})</span>}
                  </label>
                ))}
              </Card>
            )}
          </div>

          <div>
            <Label>Title *</Label>
            <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                   placeholder="e.g. Outbound Sales — Day 1" data-testid="wizard-title" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={2} value={form.description}
                      onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>

          <div>
            <Label>Learning items *</Label>
            {items.length > 0 && (
              <div className="space-y-1 mt-1 mb-2" data-testid="wizard-item-list">
                {items.map((it, i) => {
                  const Icon = TYPE_ICON[it.type] || FileText;
                  return (
                    <div key={i} className="flex items-center gap-2 text-sm bg-muted/50 rounded px-2 py-1.5">
                      <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="truncate flex-1">{it.title || it.url}</span>
                      <Badge variant="outline" className="text-[10px]">{it.type}</Badge>
                      <button onClick={() => setItems(l => l.filter((_, k) => k !== i))}
                              data-testid={`remove-item-${i}`}>
                        <X className="w-3.5 h-3.5 text-muted-foreground" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-1">
              <PickerSelect label="Add module (PPT)" options={modules}
                            getId={m => m.module_id} getLabel={m => m.title}
                            onPick={m => addItem({ type: 'module', ref_id: m.module_id, title: m.title })}
                            testid="picker-module" />
              <PickerSelect label="Add document (PDF)" options={documents}
                            getId={d => d.document_id} getLabel={d => d.name}
                            onPick={d => addItem({ type: 'document', ref_id: d.document_id, title: d.name })}
                            testid="picker-document" />
              <PickerSelect label="Add video" options={videos}
                            getId={v => v.file_id} getLabel={v => v.original_filename || v.filename}
                            onPick={v => addItem({ type: 'video', ref_id: v.file_id, title: v.original_filename || v.filename })}
                            testid="picker-video" />
            </div>
            <div className="flex gap-2 mt-2 items-center">
              <div className="flex-1">
                <PickerSelect label="Add SCORM / xAPI course" options={scormPkgs}
                              getId={p => p.package_id}
                              getLabel={p => `${p.title} (${p.package_type === 'xapi' ? 'xAPI' : 'SCORM 1.2'})`}
                              onPick={p => addItem({ type: 'scorm', ref_id: p.package_id, title: p.title })}
                              testid="picker-scorm" />
              </div>
              <label className="shrink-0">
                <input type="file" accept=".zip" className="hidden"
                       onChange={e => { uploadScorm(e.target.files?.[0]); e.target.value = ''; }}
                       data-testid="scorm-upload-input" />
                <Button type="button" variant="outline" size="sm" asChild disabled={scormUploading}>
                  <span className="cursor-pointer" data-testid="scorm-upload-btn">
                    <UploadCloud className="w-3.5 h-3.5 mr-1" />
                    {scormUploading ? 'Uploading…' : 'Upload .zip'}
                  </span>
                </Button>
              </label>
            </div>
            <div className="flex gap-2 mt-2 items-end">
              <div className="w-28">
                <Select value={linkDraft.type} onValueChange={v => setLinkDraft(d => ({ ...d, type: v }))}>
                  <SelectTrigger data-testid="link-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="link">Link</SelectItem>
                    <SelectItem value="youtube">YouTube</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Input placeholder="https://…" value={linkDraft.url}
                     onChange={e => setLinkDraft(d => ({ ...d, url: e.target.value }))}
                     data-testid="link-url" />
              <Input placeholder="Title (optional)" value={linkDraft.title}
                     onChange={e => setLinkDraft(d => ({ ...d, title: e.target.value }))}
                     className="w-40" data-testid="link-title" />
              <Button type="button" variant="outline" size="sm"
                      onClick={() => {
                        if (!linkDraft.url.trim()) { toast.error('Enter a URL'); return; }
                        addItem({ ...linkDraft, url: linkDraft.url.trim() });
                        setLinkDraft({ type: 'link', url: '', title: '' });
                      }}
                      data-testid="add-link-btn">Add</Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Due date</Label>
              <Input type="date" value={form.due_date}
                     onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
                     data-testid="wizard-due-date" />
            </div>
            <div>
              <Label>Quiz (optional gate)</Label>
              <Select value={form.quiz_id || NONE} onValueChange={v => setForm(f => ({ ...f, quiz_id: v }))}>
                <SelectTrigger data-testid="wizard-quiz"><SelectValue placeholder="No quiz" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>No quiz</SelectItem>
                  {quizzes.map(q => <SelectItem key={q.quiz_id} value={q.quiz_id}>{q.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>Cancel</Button>
          <Button onClick={onSave} disabled={saving} data-testid="wizard-publish-btn">
            {saving ? 'Publishing…' : 'Publish'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PickerSelect({ label, options, getId, getLabel, onPick, testid }) {
  return (
    <Select value={undefined} onValueChange={(id) => {
      const opt = options.find(o => getId(o) === id);
      if (opt) onPick(opt);
    }}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent>
        {options.length === 0
          ? <SelectItem value="__empty__" disabled>None available</SelectItem>
          : options.map(o => <SelectItem key={getId(o)} value={getId(o)}>{getLabel(o)}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function DetailDialog({ assignmentId, onClose }) {
  const { get } = useApi();
  const [data, setData] = useState(null);

  useEffect(() => {
    get(`/learning-assignments/${assignmentId}`).then(setData)
      .catch(() => toast.error('Failed to load detail'));
  }, [get, assignmentId]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="assignment-detail-dialog">
        <DialogHeader><DialogTitle>{data?.title || 'Assignment'}</DialogTitle></DialogHeader>
        {!data ? <p className="text-sm text-muted-foreground">Loading…</p> : (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">
              {data.items?.length} items
              {data.due_date && <> · Due {new Date(data.due_date).toLocaleDateString()}</>}
              {' '}· {data.completed_count}/{data.target_count} completed
              {data.quiz_id && ' · Quiz attached'}
            </p>
            <Table data-testid="trainee-progress-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Trainee</TableHead>
                  <TableHead className="w-44">Progress</TableHead>
                  <TableHead className="w-24">Quiz</TableHead>
                  <TableHead className="w-24">Time</TableHead>
                  <TableHead className="w-28">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data.trainee_progress || []).map(r => (
                  <TableRow key={r.trainee_id} data-testid={`progress-row-${r.trainee_id}`}>
                    <TableCell className="font-medium">
                      {r.name}
                      {r.employee_id && <span className="block text-xs text-muted-foreground">{r.employee_id}</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress value={r.pct} className="h-2" />
                        <span className="text-xs w-9 text-right">{Math.round(r.pct)}%</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{r.quiz_score != null ? `${r.quiz_score}%` : '—'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {r.time_spent_seconds ? `${Math.round(r.time_spent_seconds / 60)}m` : '—'}
                    </TableCell>
                    <TableCell>
                      {r.completed
                        ? <Badge className="bg-emerald-100 text-emerald-800 border-0">Completed</Badge>
                        : r.started
                          ? <Badge className="bg-warm-200 text-warm-800 border-0">In Progress</Badge>
                          : <Badge className="bg-slate-100 text-slate-700 border-0">Not Started</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
